import React, { useCallback, useContext, useEffect, useState } from 'react';
import { StepupInfo, StepupOffer } from '@wwe-portal/entities/stepup.type';
import { useStepupModalOpen } from '../lib/use-stepup-modal-offer';
import {
  OfferCardStatusEnum,
  StepupOfferCardProps,
} from '@wwe-portal/ui/features/offer';
import { useStepupSourceContext } from './stepup-source';

import { showDialog } from '@wwe-portal/ui/components/dialog';
import { useInvalidateStepupStatus } from '@wwe-portal/web/providers/queries/stepup';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import { useGetWebcoins } from '@wwe-portal/web/providers/queries/currency';
import { NotEnoughWebCoinDialog } from '../../club-store/modals/NotEnoughWebCoinDialog';
import { usePurchaseStepup } from '@wwe-portal/web/providers/queries/stepup.mutation';

interface StepupContextValues {
  isAuthenticated: boolean;
  isModalOpen: boolean;
  offerReviewOpen: boolean;
  processingModalOpen: boolean;
  refetchStatusInterval: number;
  modalClosedAt: Date | undefined;

  offers: StepupOffer[];
  stepupInfo: StepupInfo;

  isPaying: boolean;
  availableItems: number;
  claimedItems: number;

  focusedOffer: { offer: StepupOffer; props: StepupOfferCardProps } | undefined;

  openModal: () => void;
  closeModal: () => void;
  clearModalClosedAt: () => void;
  clearSnapClaimedItems: () => void;

  onOfferClick: (
    itemData: { offer: StepupOffer; props: StepupOfferCardProps },
    index: number
  ) => void;
  onStartOfferPurchasing: (offer: StepupOffer) => void;
  closeReviewModal: () => void;
  validatePurchase: ({ stepupOffer }: { stepupOffer?: StepupOffer }) => void;
}

const StepupContext = React.createContext<StepupContextValues>({} as never);

const useContextValues: (props: {
  refetchStatusInterval: number;
}) => StepupContextValues = ({ refetchStatusInterval }) => {
  const { stepupInfo, offers, claimedItems, availableItems } =
    useStepupSourceContext();

  // is modal open
  const [open, setOpen] = useStepupModalOpen();
  const [modalClosedAt, setModalClosedAt] = useState<Date>();
  const [snapClaimedItems, setSnapClaimedItems] = useState<
    number | undefined
  >();

  const clearSnapClaimedItems = useCallback(() => {
    setSnapClaimedItems(undefined);
  }, []);

  const handleVerifyPaymentSuccess = useCallback(
    ({ orderId }: { orderId: number }) => {
      setFocusedOffer(undefined);
      const index = offers.findIndex((offer) => offer.orderId === orderId);
      if (index !== -1) {
        setSnapClaimedItems(index);
      }
    },
    [offers]
  );

  const { startPurchase, isPaying, processingModal } = usePurchaseStepup({
    refetchStatusInterval,
    stepsUpId: stepupInfo.stepsUpId,
    onVerifySuccess: handleVerifyPaymentSuccess,
    onCloseSuccessDialog: () => {
      setTimeout(() => {
        clearSnapClaimedItems();
      }, 500);
    },
  });

  const invalidateStatus = useInvalidateStepupStatus(stepupInfo.stepsUpId);
  const { amount: currentUserWebCoins } = useGetWebcoins();

  const [focusedOffer, setFocusedOffer] = useState<{
    offer: StepupOffer;
    props: StepupOfferCardProps;
  }>();

  const onOfferClick = (data: {
    offer: StepupOffer;
    props: StepupOfferCardProps;
  }) => {
    if (data.props.status === OfferCardStatusEnum.CLAIMED) return;
    setFocusedOffer(data);
  };
  const isAuthenticated = useIsAuthenticated();

  const openModal = useCallback(() => {
    setOpen(true);
    clearSnapClaimedItems();
  }, [setOpen, clearSnapClaimedItems]);

  const closeModal = () => {
    setModalClosedAt(new Date());
    setOpen(false);
  };

  const clearModalClosedAt = useCallback(() => {
    setModalClosedAt(undefined);
  }, []);

  const closeReviewModal = () => {
    setFocusedOffer(undefined);
    invalidateStatus();
  };

  const onStartOfferPurchasing = (offer: StepupOffer) => {
    if (stepupInfo?.stepsUpId == null || offer == null) {
      addDDError(new Error('user purchase invalid step-up offer'));
      return;
    }

    startPurchase({
      stepupOffer: offer,
      stepupId: String(stepupInfo.stepsUpId),
    });
  };

  const validatePurchase = ({
    stepupOffer = focusedOffer?.offer,
  }: {
    stepupOffer?: StepupOffer;
  }) => {
    if (!stepupOffer) return;
    if (
      (stepupOffer.offerData.AlternativeCostAmount ?? 0) >
      (currentUserWebCoins ?? 0)
    ) {
      closeModal();
      closeReviewModal();
      showDialog(NotEnoughWebCoinDialog);
      return;
    }
    onStartOfferPurchasing(stepupOffer);
  };

  useEffect(() => {
    if (isAuthenticated) {
      setModalClosedAt(undefined);
    }
  }, [isAuthenticated]);

  const value: StepupContextValues = {
    isAuthenticated,
    isModalOpen: open,
    processingModalOpen: processingModal.visible,

    openModal: openModal,
    closeModal,
    clearModalClosedAt,
    modalClosedAt,
    offers,
    stepupInfo,
    onOfferClick,

    availableItems: availableItems,
    claimedItems: snapClaimedItems ?? claimedItems,
    clearSnapClaimedItems,

    focusedOffer,
    offerReviewOpen: focusedOffer != null,

    refetchStatusInterval,
    isPaying,
    onStartOfferPurchasing,
    closeReviewModal,
    validatePurchase,
  };

  return value;
};

interface StepupContextProviderProps {
  children: React.ReactNode;
  refetchStatusInterval: number;
}
export const StepupContextProvider = ({
  children,
  refetchStatusInterval,
}: StepupContextProviderProps) => {
  const value = useContextValues({ refetchStatusInterval });
  return (
    <StepupContext.Provider value={value}>{children}</StepupContext.Provider>
  );
};

export const useStepupContext = () => {
  return useContext(StepupContext);
};
